<script>
  import { onMount } from 'svelte';
  import { animeAPI, authAPI } from '../lib/api';
  import { isAuthenticated, setToken, removeToken } from '../lib/auth';
  import Loading from '../components/Loading.svelte';

  let authenticated = false;
  let loading = true;
  let animes = [];
  let username = '';
  let password = '';
  let loginError = '';
  let showAnimeModal = false;
  let showEpisodeModal = false;
  let selectedAnime = null;
  let editingAnime = null;

  let animeForm = {
    name: '',
    title: '',
    description: '',
    coverImage: '',
    bannerImage: '',
    genres: '',
    status: 'ongoing',
    rating: 0,
    releaseYear: new Date().getFullYear(),
    totalEps: 0
  };

  let episodeForm = {
    episodeNum: 1,
    title: '',
    videoUrl: '',
    thumbnail: '',
    duration: '24:00'
  };

  onMount(async () => {
    authenticated = isAuthenticated();
    if (authenticated) {
      await loadAnimes();
    }
    loading = false;
  });

  async function handleLogin() {
    try {
      loginError = '';
      const response = await authAPI.login({ username, password });
      const token = response.data?.token;
      if (token) {
        setToken(token);
        authenticated = true;
        await loadAnimes();
      }
    } catch (error) {
      console.error('Login error:', error);
      loginError = error.response?.data?.message || 'Login failed. Please check your credentials.';
      password = '';
    }
  }

  function handleLogout() {
    removeToken();
    authenticated = false;
    animes = [];
  }

  async function loadAnimes() {
    try {
      const response = await animeAPI.getAll();
      animes = response.data || [];
    } catch (error) {
      console.error('Failed to load animes:', error);
      animes = [];
    }
  }

  function openAnimeModal(anime = null) {
    editingAnime = anime;
    if (anime) {
      animeForm = { ...anime };
    } else {
      animeForm = {
        name: '',
        title: '',
        description: '',
        coverImage: '',
        bannerImage: '',
        genres: '',
        status: 'ongoing',
        rating: 0,
        releaseYear: new Date().getFullYear(),
        totalEps: 0
      };
    }
    showAnimeModal = true;
  }

  function openEpisodeModal(anime) {
    selectedAnime = anime;
    episodeForm = {
      episodeNum: 1,
      title: '',
      videoUrl: '',
      thumbnail: '',
      duration: '24:00'
    };
    showEpisodeModal = true;
  }

  async function saveAnime() {
    try {
      if (editingAnime) {
        await animeAPI.update(editingAnime.id, animeForm);
      } else {
        await animeAPI.create(animeForm);
      }
      showAnimeModal = false;
      await loadAnimes();
    } catch (error) {
      console.error('Failed to save anime:', error);
      alert(error.response?.data?.message || 'Failed to save anime');
    }
  }

  async function deleteAnime(anime) {
    if (confirm(`Are you sure you want to delete ${anime.title}?`)) {
      try {
        await animeAPI.delete(anime.id);
        await loadAnimes();
      } catch (error) {
        console.error('Failed to delete anime:', error);
        alert('Failed to delete anime');
      }
    }
  }

  async function saveEpisode() {
    try {
      await animeAPI.addEpisode(selectedAnime.id, episodeForm);
      showEpisodeModal = false;
      alert('Episode added successfully!');
    } catch (error) {
      console.error('Failed to add episode:', error);
      alert(error.response?.data?.message || 'Failed to add episode');
    }
  }
</script>

{#if loading}
  <Loading />
{:else if !authenticated}
  <div class="min-h-screen flex items-center justify-center px-4">
    <div class="card max-w-md w-full p-8">
      <h1 class="text-3xl font-bold mb-6 text-center">Admin Login</h1>
      {#if loginError}
        <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {loginError}
        </div>
      {/if}
      <div class="mb-4">
        <label class="block text-sm font-medium mb-1">Username</label>
        <input type="text" bind:value={username} class="w-full p-2 border rounded">
      </div>
      <div class="mb-6">
        <label class="block text-sm font-medium mb-1">Password</label>
        <input type="password" bind:value={password} class="w-full p-2 border rounded">
      </div>
      <button on:click={handleLogin} class="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700">
        Login
      </button>
    </div>
  </div>
{:else}
  <div class="min-h-screen bg-gray-100">
    <nav class="bg-white shadow">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex items-center">
            <h1 class="text-xl font-bold">Admin Panel</h1>
          </div>
          <div class="flex items-center">
            <button on:click={handleLogout} class="text-gray-700 hover:text-gray-900">
              Logout
            </button>
          </div>
        </div>
      </div>
    </nav>

    <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold">Anime List</h2>
        <button on:click={() => openAnimeModal()} class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          Add New Anime
        </button>
      </div>

      <div class="bg-white shadow rounded-lg overflow-hidden">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            {#each animes as anime}
              <tr>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="flex-shrink-0 h-10 w-10">
                      <img class="h-10 w-10 rounded-full" src={anime.coverImage} alt={anime.title}>
                    </div>
                    <div class="ml-4">
                      <div class="text-sm font-medium text-gray-900">{anime.title}</div>
                      <div class="text-sm text-gray-500">{anime.genres?.join(', ')}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    {anime.status}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <button on:click={() => openAnimeModal(anime)} class="text-indigo-600 hover:text-indigo-900 mr-4">Edit</button>
                  <button on:click={() => openEpisodeModal(anime)} class="text-green-600 hover:text-green-900 mr-4">Add Episode</button>
                  <button on:click={() => deleteAnime(anime)} class="text-red-600 hover:text-red-900">Delete</button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>
  </div>

  {#if showAnimeModal}
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div class="p-6">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-xl font-bold">{editingAnime ? 'Edit Anime' : 'Add New Anime'}</h2>
            <button on:click={() => showAnimeModal = false} class="text-gray-500 hover:text-gray-700">
              &times;
            </button>
          </div>
          
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Title</label>
              <input type="text" bind:value={animeForm.title} class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Description</label>
              <textarea bind:value={animeForm.description} rows="3" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm"></textarea>
            </div>
            
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Cover Image URL</label>
                <input type="text" bind:value={animeForm.coverImage} class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Banner Image URL</label>
                <input type="text" bind:value={animeForm.bannerImage} class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
              </div>
            </div>
            
            <div class="grid grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Status</label>
                <select bind:value={animeForm.status} class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="upcoming">Upcoming</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Rating</label>
                <input type="number" bind:value={animeForm.rating} min="0" max="10" step="0.1" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Release Year</label>
                <input type="number" bind:value={animeForm.releaseYear} class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Genres (comma separated)</label>
              <input type="text" bind:value={animeForm.genres} class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
            </div>
            
            <div class="flex justify-end space-x-3 pt-4">
              <button on:click={() => showAnimeModal = false} class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button on:click={saveAnime} class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">
                {editingAnime ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  {/if}

  {#if showEpisodeModal}
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div class="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div class="p-6">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-xl font-bold">Add Episode to {selectedAnime?.title}</h2>
            <button on:click={() => showEpisodeModal = false} class="text-gray-500 hover:text-gray-700">
              &times;
            </button>
          </div>
          
          <div class="space-y-4">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Episode Number</label>
                <input type="number" bind:value={episodeForm.episodeNum} min="1" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Duration (HH:MM:SS)</label>
                <input type="text" bind:value={episodeForm.duration} placeholder="24:00" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Title</label>
              <input type="text" bind:value={episodeForm.title} class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Video URL</label>
              <input type="text" bind:value={episodeForm.videoUrl} class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">Thumbnail URL</label>
              <input type="text" bind:value={episodeForm.thumbnail} class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
            </div>
            
            <div class="flex justify-end space-x-3 pt-4">
              <button on:click={() => showEpisodeModal = false} class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button on:click={saveEpisode} class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700">
                Add Episode
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  {/if}
{/if}
